import CloseIcon from "@mui/icons-material/Close"
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  FormLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { appConfig } from "../appConfig"

import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import GroupIcon from "@mui/icons-material/Group"
import SettingsIcon from "@mui/icons-material/Settings"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser"
import { SelectInputProps } from "@mui/material/Select/SelectInput"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { User } from "shared/user"
import i18n from "src/i18n/i18n"
import { useAppSelector } from "../app/hooks"
import defaultAvatar from "../assets/default_avatar.png"
import { selectMe, selectOnlinePresence } from "../features/me/meSlice"
import { useAuthStatus } from "../hooks/useAuthStatus"
import { UnstyledLink } from "./UnstyledLink"

export type UsMenuItem = {
  labelKey: string
  icon: JSX.Element
  url: string
  divider: boolean
  disabled: boolean
  visible?: boolean
}
export const usMenuItems = (user: User) => {
  return [
    {
      labelKey: "profile",
      icon: <AccountCircleIcon />,
      url: "/us/profile",
      divider: false,
      disabled: false,
      visible: true,
    },
    {
      labelKey: "organizations",
      icon: <GroupIcon />,
      url: "/us/organizations",
      divider: false,
      disabled: false,
      visible: true,
    },
    {
      labelKey: "adminApp",
      icon: <VerifiedUserIcon />,
      url: "/sa/home",
      divider: false,
      disabled: false,
      visible: !!user.isSuperAdmin,
    },
    {
      labelKey: "settings",
      icon: <SettingsIcon />,
      url: "/us/settings",
      divider: false,
      disabled: true,
      visible: true,
    },
    {
      labelKey: "support",
      icon: <SupportAgentIcon />,
      url: "/support",
      divider: true,
      disabled: true,
      visible: true,
    },
    {
      labelKey: "signout",
      icon: <ExitToAppIcon />,
      url: "/signout",
      divider: false,
      disabled: false,
      visible: true,
    },
  ] as UsMenuItem[]
}

export type UserDrawerProps = {
  onClose: () => void
}

export const UserDrawer: FC<UserDrawerProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const { imageUrl } = useAuthStatus()
  const { user } = useSelector(selectMe)
  const onlinePresence = useAppSelector(selectOnlinePresence)

  const handleLanguageChange: SelectInputProps<unknown>["onChange"] = (
    event,
  ) => {
    const newLanguage = event.target.value as string
    localStorage.setItem("language", newLanguage)
    i18n.changeLanguage(newLanguage)
    // for moment, only 'fr' and 'en' are supported; everything else will be 'en'
    moment.locale(newLanguage === "fr" ? "fr" : "en")
  }

  const fullName = `${user?.firstName} ${user?.lastName}`
  return (
    <Drawer data-ui="UI-SL2W-009"
      open={true}
      onClose={onClose}
      anchor="right"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: appConfig.userDrawerWidth,
          borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
          backgroundColor: "#fbfcfe",
          backgroundImage: `
            radial-gradient(520px 320px at 100% 0%, rgba(35, 66, 133, 0.06), transparent 65%),
            radial-gradient(420px 280px at 0% 100%, rgba(148, 210, 194, 0.08), transparent 60%)
          `,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.15)",
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
        },
      }}
    >
      <Stack direction="column" sx={{ height: "100%" }}>
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                sx={{
                  "& .MuiBadge-dot": {
                    backgroundColor: onlinePresence
                      ? "success.main"
                      : "rgba(0, 0, 0, 0.3)",
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    border: "2px solid #fbfcfe",
                  },
                }}
              >
                <Avatar
                  alt="User"
                  src={imageUrl || defaultAvatar}
                  sx={{
                    width: 52,
                    height: 52,
                    boxShadow: "0 2px 8px rgba(35, 66, 133, 0.10)",
                  }}
                />
              </Badge>

              <Stack direction="column" spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: "text.primary",
                  }}
                >
                  {fullName}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.78rem",
                    color: "text.secondary",
                  }}
                >
                  {user?.email}
                </Typography>
              </Stack>
            </Stack>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "rgba(0, 0, 0, 0.55)",
                "&:hover": {
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />

        {/* Menu */}
        {user && (
          <List sx={{ px: 1.25, pt: 1.5, pb: 1, flex: 1 }}>
            {usMenuItems(user)
              .filter((i) => i.visible)
              .map((item, index) => (
                <UnstyledLink to={item.url} key={index}>
                  <ListItemButton
                    divider={item.divider}
                    disabled={item.disabled}
                    disableRipple
                    sx={{
                      borderRadius: 1.25,
                      mb: 0.25,
                      py: 0.85,
                      px: 1.25,
                      transition: "background-color 140ms ease",
                      "&:hover": {
                        bgcolor: "rgba(35, 66, 133, 0.05)",
                        "& .MuiListItemIcon-root": { color: "primary.main" },
                        "& .MuiListItemText-primary": { color: "primary.main" },
                      },
                      "&.Mui-disabled": {
                        opacity: 0.45,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: "rgba(0, 0, 0, 0.6)",
                          transition: "color 140ms ease",
                          "& svg": { fontSize: "1.2rem" },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.labelKey)}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: "text.primary",
                          letterSpacing: "-0.005em",
                        }}
                      />
                    </Stack>
                  </ListItemButton>
                </UnstyledLink>
              ))}
          </List>
        )}

        {/* Footer — language */}
        <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />
        <Box sx={{ px: 2.5, py: 2.25 }}>
          <FormLabel
            sx={{
              display: "block",
              fontSize: "0.66rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1,
            }}
          >
            {t("preferedLang")}
          </FormLabel>
          <Select
            variant="outlined"
            value={localStorage.getItem("language") || "fr"}
            size="small"
            onChange={handleLanguageChange}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
            fullWidth
            sx={{
              bgcolor: "white",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.12)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(35, 66, 133, 0.4)",
              },
              "& .MuiSelect-select": {
                fontSize: "0.875rem",
              },
            }}
          >
            <MenuItem value="en">{t("english")}</MenuItem>
            <MenuItem value="fr">{t("french")}</MenuItem>
          </Select>
        </Box>
      </Stack>
    </Drawer>
  )
}
